import { Button, defaultErrorHandling, Modal, Paging, Table, Tag, useSearchParams } from '@maxtropy/components';
import {
  apiV2BottledGasOutboundDetailsPost,
  V2BottledGasOutboundDetailsPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { Space } from 'antd';
import type { TableProps } from 'antd';
import type { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

interface OutboundDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  dateRange: [Dayjs, Dayjs];
}

interface RecordType {
  id?: string;
  outInvNum?: string;
  outInvTime?: string;
  inInvNumber?: number;
  inInvSingleTypeNumber?: string;
  reqDepartmentName?: string;
  usePlace?: string;
  receiverFullName?: string;
}

const OutboundDetailsModal = ({ visible, onClose, dateRange }: OutboundDetailsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<V2BottledGasOutboundDetailsPostResponse>();
  const pagingInfo = useSearchParams(10);
  const { searchParams, setTotalCount } = pagingInfo;
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([]);
  const [gasTypeFilter, setGasTypeFilter] = useState<string>();

  const gasFilterOptions = useMemo(
    () =>
      (data?.gasTypeStatistics ?? []).map(item => ({
        text: `${item.gasTypeName ?? '--'}（${item.totalCount ?? 0}）`,
        value: item.mateType ?? '',
      })),
    [data]
  );
  const departmentFilterOptions = useMemo(
    () =>
      (data?.departments ?? []).map(item => ({
        text: item.reqDepartmentName ?? '',
        value: item.reqDepartment ?? '',
      })),
    [data]
  );

  const fetchDetails = async () => {
    setLoading(true);
    apiV2BottledGasOutboundDetailsPost({
      page: searchParams.pageOffset,
      size: searchParams.pageSize,
      startTime: dateRange[0].format('YYYY-MM-DD'),
      endTime: dateRange[1].format('YYYY-MM-DD'),
      reqDepartmentList: departmentFilter.length > 0 ? departmentFilter : undefined,
      mateType: gasTypeFilter,
    })
      .onError(e => {
        defaultErrorHandling(e);
        return true;
      })
      .then(res => {
        setData(res);
        setTotalCount(res.total ?? 0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!visible) return;
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, dateRange, searchParams, departmentFilter, gasTypeFilter]);

  const handleTableChange: TableProps<RecordType>['onChange'] = (_pagination, filters) => {
    setDepartmentFilter((filters.reqDepartment as string[]) ?? []);
    setGasTypeFilter((filters.mateType as string[])?.[0]);
  };

  const outboundColumns = [
    {
      title: '申请部门',
      dataIndex: 'reqDepartmentName',
      key: 'reqDepartment',
      width: 120,
      filterMultiple: true,
      filteredValue: departmentFilter,
      filters: departmentFilterOptions,
    },
    { title: '出库单号', dataIndex: 'outInvNum', key: 'outInvNum', width: 140 },
    { title: '出库时间', dataIndex: 'outInvTime', key: 'outInvTime', width: 160 },
    { title: '出库条目', dataIndex: 'inInvNumber', key: 'inInvNumber', width: 90 },
    {
      title: '出库数量(瓶)',
      dataIndex: 'inInvSingleTypeNumber',
      key: 'mateType',
      width: 220,
      filterMultiple: false,
      filteredValue: gasTypeFilter ? [gasTypeFilter] : null,
      filters: gasFilterOptions,
      render: (value: string) => (
        <Space size={[4, 4]} wrap>
          {(value ?? '')
            .split(',')
            .filter(Boolean)
            .map((item, idx) => (
              <Tag key={`${item}-${idx}`} color="default">
                {item}
              </Tag>
            ))}
        </Space>
      ),
    },
    { title: '使用地点', dataIndex: 'usePlace', key: 'usePlace', width: 120 },
    { title: '分配人', dataIndex: 'operatorFullName', key: 'operatorFullName', width: 100 },
    { title: '领用人', dataIndex: 'receiverFullName', key: 'receiverFullName', width: 100 },
  ];

  return (
    <Modal
      open={visible}
      title="瓶装气体出库明细"
      onCancel={onClose}
      footer={<Button onClick={onClose}>关闭</Button>}
      width={1200}
      destroyOnClose
      styles={{ body: { height: 640 } }}
    >
      <Table
        rowKey={record => record.id ?? record.outInvNum ?? Math.random().toString(36)}
        loading={loading}
        columns={outboundColumns}
        dataSource={data?.records ?? []}
        pagination={false}
        scroll={{ y: 500 }}
        onChange={handleTableChange}
      />
      <Paging pagingInfo={pagingInfo} />
    </Modal>
  );
};

export default OutboundDetailsModal;
